/**
 * KOAI API Service
 * 백엔드 API 통신 서비스
 */

import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:11480/api';

// Axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 180000, // 3분 (LLM 응답 대기)
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const reqUrl = error.config?.url || '';
      // 게스트 체험(ephemeral)은 인증 없이 사용 — 로그인 강제 리다이렉트 금지
      if (reqUrl.includes('/ephemeral')) {
        return Promise.reject(error);
      }
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data)
};

// Files API
export const filesAPI = {
  getAll: (params) => api.get('/files', { params }),
  getById: (id) => api.get(`/files/${id}`),
  upload: (file, teamId, folderId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (teamId) formData.append('team_id', teamId);
    if (folderId) formData.append('folder_id', folderId);
    return api.post('/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadMultiple: (files, teamId, folderId = null) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    if (teamId) formData.append('team_id', teamId);
    if (folderId) formData.append('folder_id', folderId);
    return api.post('/files/upload-multiple', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  download: (id) => api.get(`/files/${id}/download`, { responseType: 'blob' }),
  getDownloadHistory: (params) => api.get('/files/download-history', { params }),
  getStats: () => api.get('/files/stats'),
  delete: (id, permanent = false) => api.delete(`/files/${id}?permanent=${permanent}`),
  restore: (id) => api.post(`/files/${id}/restore`)
};

// Folders API
export const foldersAPI = {
  getAll: (params) => api.get('/folders', { params }),
  getById: (id) => api.get(`/folders/${id}`),
  create: (data) => api.post('/folders', data),
  update: (id, data) => api.put(`/folders/${id}`, data),
  delete: (id) => api.delete(`/folders/${id}`)
};

// RAG API
export const ragAPI = {
  getStatus: () => api.get('/rag/status'),
  query: (question, options = {}) => api.post('/rag/query', { question, ...options }),
  search: (query, options = {}) => api.post('/rag/search', { query, ...options }),
  indexFile: (fileId) => api.post(`/rag/index/${fileId}`),
  indexAll: () => api.post('/rag/index-all'),
  removeIndex: (fileId) => api.delete(`/rag/index/${fileId}`),
  cleanup: () => api.post('/rag/cleanup'),
  
  // Sessions
  getSessions: (params) => api.get('/rag/sessions', { params }),
  getSession: (id) => api.get(`/rag/sessions/${id}`),
  createSession: (data) => api.post('/rag/sessions', data),
  deleteSession: (id) => api.delete(`/rag/sessions/${id}`)
};

// Users API
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  updateTeams: (id, data) => api.put(`/users/${id}/teams`, data),
  delete: (id) => api.delete(`/users/${id}`),
  getSubscriptionHistory: (id) => api.get(`/users/${id}/subscription-history`),
  export: (params) =>
    api.get('/users/export', { params, responseType: 'blob', timeout: 60000 })
};

// Teams API
export const teamsAPI = {
  getAll: (params) => api.get('/teams', { params }),
  getById: (id) => api.get(`/teams/${id}`),
  create: (data) => api.post('/teams', data),
  update: (id, data) => api.put(`/teams/${id}`, data),
  delete: (id) => api.delete(`/teams/${id}`),
  addMember: (id, userId, role) => api.post(`/teams/${id}/members`, { userId, role }),
  removeMember: (id, userId) => api.delete(`/teams/${id}/members/${userId}`)
};

// Notices API
export const noticesAPI = {
  getAll: (params) => api.get('/notices', { params }),
  getById: (id) => api.get(`/notices/${id}`),
  create: (data) => api.post('/notices', data),
  update: (id, data) => api.put(`/notices/${id}`, data),
  delete: (id) => api.delete(`/notices/${id}`)
};

// Team Topics API
export const teamTopicsAPI = {
  getAll: () => api.get('/team-topics'),
  getById: (id) => api.get(`/team-topics/${id}`),
  create: (data) => api.post('/team-topics', data),
  update: (id, data) => api.put(`/team-topics/${id}`, data),
  delete: (id) => api.delete(`/team-topics/${id}`)
};

// Level Requests API
export const levelRequestsAPI = {
  getAll: (params) => api.get('/level-requests', { params }),
  getPendingCount: () => api.get('/level-requests/pending-count'),
  create: (data) => api.post('/level-requests', data),
  approve: (id) => api.put(`/level-requests/${id}/approve`),
  reject: (id, data) => api.put(`/level-requests/${id}/reject`, data),
  cancel: (id) => api.delete(`/level-requests/${id}`)
};

// Boards API
export const boardsAPI = {
  getAll: () => api.get('/boards'),
  getById: (id) => api.get(`/boards/${id}`),
  create: (data) => api.post('/boards', data),
  update: (id, data) => api.put(`/boards/${id}`, data),
  delete: (id) => api.delete(`/boards/${id}`)
};

// Board Posts API
export const boardPostsAPI = {
  getAll: (params) => api.get('/board-posts', { params }),
  getById: (id) => api.get(`/board-posts/${id}`),
  create: (data) => api.post('/board-posts', data),
  update: (id, data) => api.put(`/board-posts/${id}`, data),
  delete: (id) => api.delete(`/board-posts/${id}`)
};

// Health API
export const healthAPI = {
  check: () => api.get('/health')
};

// Subscription API
export const subscriptionAPI = {
  getStatus: () => api.get('/subscription/status'),
  checkout: () => api.post('/subscription/checkout'),
  /** Polar Customer Portal (구독 관리·해지) */
  portal: () => api.post('/subscription/portal'),
};

// Platform Admin API
export const platformAPI = {
  getStats: () => api.get('/platform/stats'),
  getCompanies: () => api.get('/platform/companies'),
  getLLMUsage: (params) => api.get('/platform/llm-usage', { params }),
  getMonitoring: () => api.get('/platform/monitoring'),
  getRecentUsers: (params) => api.get('/platform/recent-users', { params })
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Ephemeral RAG API
export const ephemeralAPI = {
  /** 파일 전송만 (즉시 jobId 반환). 처리 완료는 getUploadJob / uploadAndWait 사용 */
  startUpload: (files, sessionId) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('sessionId', sessionId);
    return api.post('/ephemeral/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000 // 파일 전송만
    });
  },
  getUploadJob: (jobId) => api.get(`/ephemeral/upload/job/${jobId}`, { timeout: 15000 }),
  /**
   * 업로드 + 백그라운드 처리 폴링까지 완료될 때까지 대기
   * @returns axios-like { data: { success, results, message, truncated, job } }
   */
  uploadAndWait: async (files, sessionId, { onProgress, pollInterval = 1200, maxWaitMs = 600000 } = {}) => {
    const startRes = await ephemeralAPI.startUpload(files, sessionId);
    const jobId = startRes.data?.jobId;
    if (!jobId) {
      throw new Error(startRes.data?.error || '업로드 잡을 시작하지 못했습니다.');
    }

    const startedAt = Date.now();
    while (Date.now() - startedAt < maxWaitMs) {
      const jobRes = await ephemeralAPI.getUploadJob(jobId);
      const job = jobRes.data?.data;
      if (!job) {
        throw new Error('업로드 잡 상태를 확인할 수 없습니다.');
      }
      onProgress?.(job);

      if (job.status === 'completed') {
        const successCount = (job.results || []).filter((r) => r.success).length;
        return {
          data: {
            success: successCount > 0,
            message: job.message,
            results: job.results,
            truncated: job.truncated,
            job
          }
        };
      }
      if (job.status === 'failed') {
        return {
          data: {
            success: false,
            error: job.error || job.message || '파일 처리에 실패했습니다.',
            results: job.results,
            job
          }
        };
      }
      await sleep(pollInterval);
    }
    throw new Error('문서 처리 시간이 너무 오래 걸립니다. 잠시 후 다시 시도해 주세요.');
  },
  /** 하위 호환: 기존 호출부는 폴링까지 포함한 uploadAndWait 사용 */
  upload: (files, sessionId, options) => ephemeralAPI.uploadAndWait(files, sessionId, options),
  loadSample: (sessionId) =>
    api.post('/ephemeral/sample', { sessionId }, { timeout: 180000 }),
  query: (sessionId, question, chatHistory = [], model = null) => {
    const payload = { sessionId, question, chatHistory };
    // null을 보내면 Ollama가 "model is required" 400을 반환함
    if (model) payload.model = model;
    return api.post('/ephemeral/query', payload);
  },
  exportAnswer: (data) =>
    api.post('/ephemeral/export', data, { responseType: 'blob', timeout: 60000 }),
  getSession: (sessionId) => api.get(`/ephemeral/session/${sessionId}`),
  deleteSession: (sessionId) => api.delete(`/ephemeral/session/${sessionId}`),
  removeDocument: (sessionId, documentId) =>
    api.delete(`/ephemeral/session/${sessionId}/document/${documentId}`),
  getStatus: () => api.get('/ephemeral/status')
};

// Decisions API
export const decisionsAPI = {
  create: (data) => api.post('/decisions', data),
  getAll: (params) => api.get('/decisions', { params }),
  getById: (decisionId) => api.get(`/decisions/${decisionId}`),
  approve: (decisionId, data) => api.post(`/decisions/${decisionId}/approve`, data),
  execute: (decisionId, data) => api.post(`/decisions/${decisionId}/execute`, data),
  feedback: (decisionId, data) => api.post(`/decisions/${decisionId}/feedback`, data),
  delete: (decisionId) => api.delete(`/decisions/${decisionId}`)
};

// PromptClinic API
export const promptClinicAPI = {
  optimize: (data) => api.post('/promptclinic/optimize', data),
  save: (title, inputData, resultData) => api.post('/promptclinic/save', { title, input_data: inputData, result_data: resultData }),
  getSaved: (params) => api.get('/promptclinic/saved', { params }),
  getSavedById: (id) => api.get(`/promptclinic/saved/${id}`),
  deleteSaved: (id) => api.delete(`/promptclinic/saved/${id}`),
  health: () => api.get('/promptclinic/health')
};

// DeciCraft API
export const deciCraftAPI = {
  analyze: (taskDescription) => api.post('/decicraft/analyze', { taskDescription }),
  executePrompt: (data) => api.post('/decicraft/execute-prompt', data),
  savePrompt: (data) => api.post('/decicraft/save-prompt', data),
  save: (title, inputData, resultData) => api.post('/decicraft/save', { title, input_data: inputData, result_data: resultData }),
  getSaved: (params) => api.get('/decicraft/saved', { params }),
  getSavedById: (id) => api.get(`/decicraft/saved/${id}`),
  deleteSaved: (id) => api.delete(`/decicraft/saved/${id}`),
  health: () => api.get('/decicraft/health')
};

export default api;
